import { getAnalytics, isSupported } from 'firebase/analytics'
import { initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import {
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore'

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
}

// Validate Firebase configuration in development only
if (process.env.NODE_ENV === 'development') {
  const missingKeys = Object.entries(firebaseConfig)
    .filter(([, value]) => !value)
    .map(([key]) => key)

  if (missingKeys.length > 0) {
    console.warn('⚠️ Missing Firebase environment variables:', missingKeys)
  } else {
    console.log('✅ Firebase configuration loaded successfully')
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

const canUsePersistentCache = (() => {
  if (typeof window === 'undefined') return false
  if (!('indexedDB' in window)) return false
  try {
    const testKey = '__firestore_persist_test__'
    window.localStorage.setItem(testKey, '1')
    window.localStorage.removeItem(testKey)
    return true
  } catch (error) {
    console.warn('⚠️ Firestore persistent cache disabled due to storage limitations:', error?.message || error)
    return false
  }
})()

const canUseMultiTabPersistence = (() => {
  if (typeof window === 'undefined') return false
  if (!window.localStorage) return false

  // Attempt to reserve ~256KB to ensure browsers with tiny quotas fall back gracefully
  const testKey = '__firestore_multitab_test__'
  const testPayload = 'x'.repeat(256 * 1024)

  try {
    window.localStorage.setItem(testKey, testPayload)
    window.localStorage.removeItem(testKey)
    return true
  } catch (error) {
    console.warn('ℹ️ Firestore multi-tab persistence disabled due to limited storage:', error?.message || error)
    try {
      window.localStorage.removeItem(testKey)
    } catch (_) {
      /* noop */
    }
    return false
  }
})()

// Initialize Firestore with optimized settings and safe fallback
const db = initializeFirestore(app, {
  localCache: canUsePersistentCache
    ? persistentLocalCache({
      cacheSizeBytes: 100 * 1024 * 1024, // 100MB cache size
      ...(canUseMultiTabPersistence ? { tabManager: persistentMultipleTabManager() } : {}),
    })
    : memoryLocalCache(),
})

// Initialize Auth with persistence
const auth = getAuth(app)

// Initialize Analytics in browser environments only and only if measurement ID is provided
// isSupported() checks whether the current environment supports analytics (e.g., not Node)
let analytics = null

/**
 * Initialize Analytics only after user consent. Returns the analytics instance
 * or null if analytics is not supported or not configured.
 */
export async function initAnalytics() {
  if (analytics) return analytics

  if (typeof window === 'undefined' || !process.env.VUE_APP_FIREBASE_MEASUREMENT_ID) {
    // Not in a browser or measurement ID not configured
    return null
  }

  try {
    const supported = await isSupported()
    if (!supported) {
      console.log('ℹ️ Firebase Analytics not supported in this environment')
      return null
    }

    try {
      analytics = getAnalytics(app)
      console.log('✅ Firebase Analytics initialized')
      return analytics
    } catch (err) {
      console.warn('⚠️ Firebase Analytics failed to initialize:', err)
      return null
    }
  } catch (err) {
    console.warn('⚠️ Firebase Analytics support check failed:', err)
    return null
  }
}

export { analytics, auth, db }
export default app
